import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, CheckCircle2, Copy, Download, Edit3, FileText,
  Folder, FolderOpen, FolderPlus, ImageIcon, Loader2, MoveRight,
  RefreshCw, Search, Sparkles, Trash2, Upload, X,
} from 'lucide-react';
import { adminApi } from './AdminAuthContext';
import { API_ROOT } from '../lib/api';
import MediaFolderPane from './MediaFolderPane';
import MediaViewControls, { getMediaGridStyle, useMediaTileSize } from './MediaViewControls';

const SITE_BASE = API_ROOT.replace(/\/api$/, '');
const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/svg+xml,application/pdf';
const MAX_MB = 25;
const EMPTY_MEDIA_AI = {
  subject_type: '',
  scene_type: '',
  confidence: 0,
  visible_features: [],
  suitable_for: [],
  not_suitable_for: [],
  recommended_pages: [],
  summary: { nl: '', de: '' },
};
const EMPTY_MEDIA_META = {
  title: { nl: '', de: '' },
  alt: { nl: '', de: '' },
  description: { nl: '', de: '' },
  tags: [],
  ai: EMPTY_MEDIA_AI,
  analysis_updated_at: null,
};
const META_LANGS = [
  ['de', 'Deutsch'],
  ['nl', 'Niederlaendisch'],
];

function fmtSize(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fullUrl(rel) {
  if (!rel) return '';
  if (/^https?:\/\//i.test(rel)) return rel;
  return `${SITE_BASE}${rel}`;
}

function parentPath(path) {
  if (!path) return '';
  const parts = String(path).split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
}

function listValue(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function ensureMediaAi(ai) {
  const source = ai && typeof ai === 'object' && !Array.isArray(ai) ? ai : {};
  return {
    ...EMPTY_MEDIA_AI,
    ...source,
    visible_features: listValue(source.visible_features),
    suitable_for: listValue(source.suitable_for),
    not_suitable_for: listValue(source.not_suitable_for),
    recommended_pages: listValue(source.recommended_pages),
    summary: { ...EMPTY_MEDIA_AI.summary, ...(source.summary || {}) },
  };
}

function ensureMediaMeta(meta) {
  const ai = ensureMediaAi(meta?.ai);
  return {
    title: { ...EMPTY_MEDIA_META.title, ...(meta?.title || {}) },
    alt: { ...EMPTY_MEDIA_META.alt, ...(meta?.alt || {}) },
    description: { ...EMPTY_MEDIA_META.description, ...(meta?.description || {}) },
    tags: Array.isArray(meta?.tags) ? meta.tags : [],
    ai,
    analysis_updated_at: meta?.analysis_updated_at || null,
  };
}

function mediaMetaSearchText(item) {
  const meta = ensureMediaMeta(item?.meta);
  const ai = meta.ai;
  return [
    item?.filename,
    item?.path,
    item?.folder,
    meta.title.de,
    meta.title.nl,
    meta.alt.de,
    meta.alt.nl,
    meta.description.de,
    meta.description.nl,
    ...(meta.tags || []),
    ai.subject_type,
    ai.scene_type,
    ai.summary.de,
    ai.summary.nl,
    ...ai.visible_features,
    ...ai.suitable_for,
    ...ai.not_suitable_for,
    ...ai.recommended_pages,
  ].filter(Boolean).join(' ').toLowerCase();
}

function aiConfidence(ai) {
  const value = Number(ai?.confidence || 0);
  if (!value) return '';
  return `${Math.round(value * 100)}%`;
}

function AiChips({ items, tone = 'neutral', limit = 5 }) {
  const values = listValue(items).slice(0, limit);
  if (!values.length) return null;
  const cls = tone === 'bad'
    ? 'bg-[#C03A1A]/10 text-[#9E2D15]'
    : tone === 'good'
      ? 'bg-[#F26B1A]/10 text-[#D85A0A]'
      : 'bg-white text-[#5C5852]';
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((item) => (
        <span key={item} className={`px-2 py-1 rounded-full text-[10.5px] font-semibold border border-[#E5E0D2] ${cls}`}>
          {item}
        </span>
      ))}
    </div>
  );
}

function MediaAiSummary({ meta, compact = false }) {
  const ai = ensureMediaAi(meta?.ai);
  const hasAi = ai.subject_type || ai.scene_type || ai.summary.de || ai.visible_features.length;
  if (!hasAi) return null;
  return (
    <div className={`rounded-xl border border-[#E5E0D2] bg-white ${compact ? 'p-2' : 'p-3'} space-y-2`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#5C5852] font-semibold">KI-Erkennung</p>
        {aiConfidence(ai) && <span className="text-[10.5px] font-semibold text-[#F26B1A]">{aiConfidence(ai)}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ai.subject_type && <span className="px-2 py-1 rounded-full bg-[#1F1D1A] text-white text-[10.5px] font-semibold">{ai.subject_type}</span>}
        {ai.scene_type && <span className="px-2 py-1 rounded-full bg-[#FAF8F2] text-[#1F1D1A] text-[10.5px] font-semibold">{ai.scene_type}</span>}
      </div>
      {!compact && (ai.summary.de || ai.summary.nl) && (
        <p className="text-[12px] text-[#5C5852] leading-relaxed">{ai.summary.de || ai.summary.nl}</p>
      )}
      {!compact && (
        <div className="space-y-2">
          <AiChips items={ai.visible_features} />
          <AiChips items={ai.suitable_for} tone="good" />
          <AiChips items={ai.not_suitable_for} tone="bad" />
        </div>
      )}
    </div>
  );
}

function slugifyFilename(value, ext) {
  const base = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 90);
  return `${base || 'horrenmaker-bild'}${ext ? `.${ext}` : ''}`;
}

function suggestSeoFilename(item, meta) {
  const parts = String(item?.filename || '').split('.');
  const ext = parts.length > 1 ? parts.pop() : '';
  const source =
    meta?.alt?.de ||
    meta?.title?.de ||
    meta?.description?.de ||
    meta?.alt?.nl ||
    meta?.title?.nl ||
    meta?.description?.nl ||
    item?.filename ||
    'horrenmaker-bild';
  return slugifyFilename(source, ext);
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/45 flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-[#E5E0D2]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E5E0D2] flex items-center justify-between gap-3">
          <p className="font-display text-[20px] text-[#1F1D1A]">{title}</p>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#FAF8F2] inline-flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function UploadZone({ path, onUploaded, onError }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const uploadFiles = async (files) => {
    if (!files?.length || busy) return;
    setBusy(true);
    setProgress(0);
    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.size > MAX_MB * 1024 * 1024) {
        onError(`"${file.name}" ist groesser als ${MAX_MB} MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', path || '');
      try {
        const { data } = await adminApi.post('/admin/media/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        uploaded.push(data);
      } catch (e) {
        onError(`"${file.name}": ${e?.response?.data?.detail || 'Upload fehlgeschlagen.'}`);
      }
    }
    setBusy(false);
    setProgress(0);
    if (uploaded.length) onUploaded(uploaded);
  };

  return (
    <label
      data-testid="media-upload-zone"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles([...e.dataTransfer.files]); }}
      className={`block cursor-pointer rounded-xl border-2 border-dashed p-5 text-center transition-colors ${
        dragging ? 'border-[#F26B1A] bg-[#FFF8F0]' : 'border-[#E5E0D2] bg-white hover:border-[#F26B1A]'
      } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        data-testid="media-upload-input"
        className="hidden"
        onChange={(e) => uploadFiles([...e.target.files])}
      />
      <Upload className="w-7 h-7 mx-auto text-[#F26B1A] mb-2" />
      <p className="text-[13.5px] font-semibold text-[#1F1D1A]">
        {busy ? `Hochladen ... ${progress}%` : 'Bilder oder PDF hierher ziehen'}
      </p>
      <p className="text-[11.5px] text-[#5C5852] mt-1">JPG, PNG, WebP, GIF, SVG, PDF bis {MAX_MB} MB</p>
    </label>
  );
}

function FolderTile({ folder, onOpen, onRename, onMove, onDelete }) {
  return (
    <article className="rounded-2xl border border-[#E5E0D2] bg-white hover:border-[#F26B1A] transition-colors overflow-hidden">
      <button type="button" onClick={() => onOpen(folder.path)} className="w-full text-left p-4 min-h-[106px] flex items-start gap-3">
        <span className="w-11 h-11 rounded-xl bg-[#F26B1A]/10 text-[#F26B1A] inline-flex items-center justify-center shrink-0">
          <FolderOpen className="w-5 h-5" />
        </span>
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-[#1F1D1A] truncate">{folder.name}</span>
          <span className="block text-[10.5px] text-[#5C5852] font-mono truncate mt-1" title={folder.path}>{folder.path}</span>
          <span className="inline-flex mt-2 text-[10.5px] font-semibold text-[#F26B1A]">Oeffnen</span>
        </span>
      </button>
      <div className="px-3 pb-3 flex items-center gap-1">
        <button type="button" onClick={() => onRename(folder)} title="Umbenennen" className="media-icon-btn"><Edit3 className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => onMove(folder)} title="Verschieben" className="media-icon-btn"><MoveRight className="w-3.5 h-3.5" /></button>
        <button type="button" onClick={() => onDelete(folder)} title="Loeschen" className="media-icon-btn danger"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </article>
  );
}

function FileTile({ item, selected = false, onSelect, onCopy, onRename, onMove, onDelete }) {
  const isPdf = item.type === 'pdf';
  const url = fullUrl(item.url);
  const meta = ensureMediaMeta(item.meta);
  const previewAlt = meta.alt.de || meta.alt.nl || item.filename;
  const ai = meta.ai;
  const stop = (fn) => (e) => {
    e.stopPropagation();
    fn();
  };
  return (
    <article
      data-testid={`media-card-${item.path}`}
      onClick={() => onSelect?.(item)}
      className={`rounded-xl border bg-white overflow-hidden group cursor-pointer transition-colors ${selected ? 'border-[#F26B1A] ring-2 ring-[#F26B1A]/20' : 'border-[#E5E0D2] hover:border-[#F26B1A]'}`}
    >
      <div className="aspect-[4/3] bg-[#FAF8F2] flex items-center justify-center overflow-hidden">
        {isPdf ? (
          <div className="text-center px-3">
            <FileText className="w-10 h-10 mx-auto text-[#C03A1A]" />
            <p className="mt-2 text-[11px] font-semibold text-[#1F1D1A]">PDF</p>
          </div>
        ) : (
          <img src={url} alt={previewAlt} loading="lazy" className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]" />
        )}
      </div>
      <div className="p-3">
        <p className="font-mono text-[10.5px] text-[#1F1D1A] truncate" title={item.path}>{item.filename}</p>
        <p className="text-[10.5px] text-[#5C5852] tabular-nums mt-0.5">{fmtSize(item.size)} - {fmtDate(item.modified_at)}</p>
        {(ai.subject_type || ai.scene_type) && (
          <p className="text-[10px] text-[#F26B1A] font-semibold mt-1 truncate" title={`${ai.subject_type || ''} ${ai.scene_type || ''}`.trim()}>
            {[ai.subject_type, ai.scene_type].filter(Boolean).join(' / ')}
          </p>
        )}
        <div className="grid grid-cols-5 gap-1 mt-2.5">
          <button type="button" onClick={stop(() => onCopy(item))} title="URL kopieren" className="media-icon-btn dark"><Copy className="w-3.5 h-3.5" /></button>
          <a href={fullUrl(item.download_url || item.url)} onClick={(e) => e.stopPropagation()} title="Download" className="media-icon-btn" download><Download className="w-3.5 h-3.5" /></a>
          <button type="button" onClick={stop(() => onRename(item))} title="Umbenennen" className="media-icon-btn"><Edit3 className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={stop(() => onMove(item))} title="Verschieben" className="media-icon-btn"><MoveRight className="w-3.5 h-3.5" /></button>
          <button type="button" onClick={stop(() => onDelete(item))} title="Loeschen" className="media-icon-btn danger"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </article>
  );
}

export default function AdminMediaPage() {
  const [listing, setListing] = useState({ path: '', parent: null, breadcrumbs: [], folders: [], files: [] });
  const [folderTree, setFolderTree] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [mobilePane, setMobilePane] = useState('folders');
  const [tileSize, setTileSize] = useMediaTileSize('horrenmaker.admin.media.tileSize');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [query, setQuery] = useState('');
  const [dialog, setDialog] = useState(null);
  const [selected, setSelected] = useState(null);
  const [metaDraft, setMetaDraft] = useState(EMPTY_MEDIA_META);
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaGenerating, setMetaGenerating] = useState(false);
  const [seoFilename, setSeoFilename] = useState('');

  const showToast = useCallback((msg, kind = 'ok') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2600);
  }, []);

  const load = useCallback(async (path = currentPath) => {
    setLoading(true);
    setError(null);
    setSelected(null);
    try {
      const { data } = await adminApi.get('/admin/media/browse', { params: { path } });
      setListing(data);
      setCurrentPath(data.path || '');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Medienpool konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [currentPath]);

  const loadTree = useCallback(async () => {
    try {
      const { data } = await adminApi.get('/admin/media/tree');
      setFolderTree(Array.isArray(data?.folders) ? data.folders : []);
    } catch (e) {
      setFolderTree([]);
    }
  }, []);

  useEffect(() => { loadTree(); load(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openFolder = useCallback(async (path) => {
    await load(path);
    setMobilePane('files');
  }, [load]);

  const selectFile = useCallback((item) => {
    setSelected(item);
    setMobilePane('details');
  }, []);

  useEffect(() => {
    setMetaDraft(ensureMediaMeta(selected?.meta));
    setSeoFilename('');
  }, [selected]);

  const filteredFolders = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return listing.folders || [];
    return (listing.folders || []).filter((f) => `${f.name} ${f.path}`.toLowerCase().includes(q));
  }, [listing.folders, query]);

  const filteredFiles = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return listing.files || [];
    return (listing.files || []).filter((f) => mediaMetaSearchText(f).includes(q));
  }, [listing.files, query]);
  const visibleFiles = currentPath === '' ? [] : filteredFiles;
  const mediaGridStyle = useMemo(() => getMediaGridStyle(tileSize), [tileSize]);
  const moveTargetOptions = useMemo(() => {
    if (dialog?.kind !== 'move') return folderTree;
    const itemPath = dialog.item?.path || '';
    const movingFolder = dialog.item && dialog.item.type !== 'image' && dialog.item.type !== 'pdf';
    return folderTree.filter((folder) => {
      if (!movingFolder) return true;
      return folder.path !== itemPath && !folder.path.startsWith(`${itemPath}/`);
    });
  }, [dialog, folderTree]);

  const createFolder = async (name) => {
    const { data } = await adminApi.post('/admin/media/folder', { parent: currentPath, name });
    showToast(`Ordner angelegt: ${data.name}`);
    await loadTree();
    await load(currentPath);
  };

  const renameItem = async (item, name) => {
    if (item.type === 'pdf' || item.type === 'image') {
      await adminApi.post('/admin/media/file/rename', { path: item.path, name });
    } else {
      await adminApi.patch('/admin/media/folder', { path: item.path, name });
    }
    showToast('Umbenannt.');
    await loadTree();
    await load(currentPath);
  };

  const moveItem = async (item, targetDir) => {
    await adminApi.post('/admin/media/file/move', { path: item.path, targetDir });
    showToast('Verschoben. Falls live verwendet: im Editor neu auswaehlen.');
    await loadTree();
    await load(currentPath);
  };

  const deleteFile = async (item) => {
    if (!window.confirm(`"${item.filename}" wirklich loeschen?`)) return;
    await adminApi.delete('/admin/media/file', { data: { path: item.path } });
    showToast('Datei geloescht.');
    await load(currentPath);
  };

  const deleteFolder = async (item) => {
    if (!window.confirm(`Ordner "${item.name}" inklusive Inhalt wirklich loeschen?`)) return;
    await adminApi.delete('/admin/media/folder', { data: { path: item.path } });
    showToast('Ordner geloescht.');
    await loadTree();
    await load(currentPath);
  };

  const copyUrl = async (item) => {
    await navigator.clipboard.writeText(item.url);
    showToast(`URL kopiert: ${item.filename}`);
  };

  const updateMetaDraft = (group, lang, value) => {
    setMetaDraft((current) => ({
      ...current,
      [group]: {
        ...(current[group] || {}),
        [lang]: value,
      },
    }));
  };

  const updateTags = (value) => {
    setMetaDraft((current) => ({
      ...current,
      tags: value.split(/[,;\n]+/).map((tag) => tag.trim()).filter(Boolean),
    }));
  };

  const saveSelectedMeta = async () => {
    if (!selected) return;
    setMetaSaving(true);
    try {
      const { data } = await adminApi.put('/admin/media/meta', { path: selected.path, meta: metaDraft });
      const nextSelected = { ...selected, meta: data.meta };
      setSelected(nextSelected);
      setListing((current) => ({
        ...current,
        files: (current.files || []).map((item) => (item.path === selected.path ? { ...item, meta: data.meta } : item)),
      }));
      showToast('Bild-SEO gespeichert.');
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Bild-SEO konnte nicht gespeichert werden.', 'err');
    } finally {
      setMetaSaving(false);
    }
  };

  const generateSelectedMeta = async () => {
    if (!selected || selected.type === 'pdf') return;
    setMetaGenerating(true);
    try {
      const { data } = await adminApi.post('/admin/media/meta/suggest', { path: selected.path }, { timeout: 90000 });
      const nextMeta = ensureMediaMeta(data.meta);
      setMetaDraft(nextMeta);
      setSeoFilename(data.filename || suggestSeoFilename(selected, nextMeta));
      setSelected((prev) => (prev ? { ...prev, meta: nextMeta } : prev));
      setListing((prev) => ({
        ...prev,
        files: prev.files.map((file) => (file.path === selected.path ? { ...file, meta: nextMeta } : file)),
      }));
      showToast('KI-Vorschlag erstellt. Bitte pruefen und speichern.');
    } catch (e) {
      showToast(e?.response?.data?.detail || 'KI-Vorschlag fehlgeschlagen.', 'err');
    } finally {
      setMetaGenerating(false);
    }
  };

  const submitDialog = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const value = String(fd.get('value') || '').trim();
    if (!value && dialog.kind !== 'move') return;
    try {
      if (dialog.kind === 'folder') await createFolder(value);
      if (dialog.kind === 'rename') await renameItem(dialog.item, value);
      if (dialog.kind === 'move') await moveItem(dialog.item, value);
      setDialog(null);
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Aktion fehlgeschlagen.', 'err');
    }
  };

  return (
    <div data-testid="admin-media-page" className="w-full max-w-none">
      <style>{`
        .media-icon-btn { height: 30px; border-radius: 999px; display: inline-flex; align-items: center; justify-content: center; color: #5C5852; background: #FAF8F2; transition: all .18s ease; }
        .media-icon-btn:hover { color: #F26B1A; background: #FFF0E4; }
        .media-icon-btn.dark { color: #fff; background: #1F1D1A; }
        .media-icon-btn.dark:hover { background: #F26B1A; color: #fff; }
        .media-icon-btn.danger:hover { color: #C03A1A; background: rgba(192,58,26,.10); }
      `}</style>

      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-10 h-10 rounded-full bg-[#F26B1A]/10 text-[#F26B1A] items-center justify-center">
            <ImageIcon className="w-5 h-5" />
          </span>
          <div>
            <h1 className="font-display text-[24px] lg:text-[30px] text-[#1F1D1A] leading-none">Medienpool</h1>
            <p className="text-[12.5px] text-[#5C5852] mt-1">Bilder und PDF zentral unter /medienpool verwalten.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setDialog({ kind: 'folder' })} className="pill pill-leaf">
            <FolderPlus className="w-4 h-4" /> Ordner
          </button>
          <button type="button" onClick={() => load(currentPath)} className="pill">
            <RefreshCw className="w-4 h-4" /> Aktualisieren
          </button>
        </div>
      </div>

      <div className="lg:hidden grid grid-cols-3 gap-2 mb-3">
        {[
          ['folders', 'Ordner'],
          ['files', 'Dateien'],
          ['details', 'Details'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setMobilePane(id)}
            className={`px-3 py-2 rounded-full text-[12px] font-semibold border ${mobilePane === id ? 'bg-[#F26B1A] border-[#F26B1A] text-white' : 'bg-white border-[#E5E0D2] text-[#5C5852]'}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] 2xl:grid-cols-[300px_minmax(0,1fr)_340px] gap-4 2xl:h-[calc(100vh-170px)] min-h-0">
        <MediaFolderPane
          folders={folderTree}
          currentPath={currentPath}
          onOpen={openFolder}
          className={`${mobilePane === 'folders' ? 'flex' : 'hidden'} lg:flex bg-white rounded-xl border border-[#E5E0D2] overflow-hidden max-h-[calc(100vh-230px)] min-h-[420px] lg:max-h-none lg:min-h-[620px] 2xl:min-h-0`}
        />

        <section className={`${mobilePane === 'files' ? 'flex' : 'hidden'} lg:flex bg-white rounded-xl border border-[#E5E0D2] overflow-hidden min-h-[520px] lg:min-h-[620px] 2xl:min-h-0 flex-col`}>
          <div className="p-4 border-b border-[#E5E0D2] flex flex-col xl:flex-row xl:items-center justify-between gap-3 shrink-0">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold">Aktueller Ordner</p>
              <p className="font-mono text-[13px] text-[#1F1D1A] mt-1 truncate">/medienpool{currentPath ? `/${currentPath}` : ''}</p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <MediaViewControls value={tileSize} onChange={setTileSize} />
              <label className="relative w-full sm:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5852]" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="In diesem Ordner suchen"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]"
                />
              </label>
            </div>
          </div>

          {error && (
            <p data-testid="media-err" className="m-4 inline-flex items-start gap-2 text-[12.5px] text-[#C03A1A]">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
            </p>
          )}

          <div className="p-4 overflow-y-auto min-h-0 flex-1">
            {loading ? (
              <div className="p-14 text-center text-[#5C5852]"><Loader2 className="inline w-5 h-5 animate-spin" /></div>
            ) : (
              <div className="space-y-5">
                {currentPath && (
                  <button type="button" onClick={() => openFolder(parentPath(currentPath))}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#FAF8F2] text-[#5C5852] text-[12px] font-semibold hover:text-[#F26B1A]">
                    <Folder className="w-4 h-4" /> Eine Ebene hoeher
                  </button>
                )}

                {filteredFolders.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold mb-2">Ordner</p>
                    <div className="grid gap-3" style={mediaGridStyle}>
                      {filteredFolders.map((folder) => (
                        <FolderTile
                          key={folder.path}
                          folder={folder}
                          onOpen={openFolder}
                          onRename={(item) => setDialog({ kind: 'rename', item, label: 'Neuer Ordnername', initial: item.name })}
                          onMove={(item) => setDialog({ kind: 'move', item, label: 'Zielordner', initial: parentPath(item.path) })}
                          onDelete={deleteFolder}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {visibleFiles.length > 0 && (
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold mb-2">Dateien</p>
                    <div data-testid="media-grid" className="grid gap-3" style={mediaGridStyle}>
                      {visibleFiles.map((item) => (
                        <FileTile
                          key={item.path}
                          item={item}
                          selected={selected?.path === item.path}
                          onSelect={selectFile}
                          onCopy={copyUrl}
                          onRename={(it) => setDialog({ kind: 'rename', item: it, label: 'Neuer Dateiname', initial: it.filename })}
                          onMove={(it) => setDialog({ kind: 'move', item: it, label: 'Zielordner', initial: it.folder || '' })}
                          onDelete={deleteFile}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {filteredFolders.length === 0 && visibleFiles.length === 0 && (
                  <p data-testid="media-empty" className="p-12 text-center text-[14px] text-[#5C5852] bg-[#FAF8F2] rounded-xl">
                    Dieser Ordner ist leer.
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        <aside className={`${mobilePane === 'details' ? 'flex' : 'hidden'} lg:flex lg:col-span-2 2xl:col-span-1 bg-white rounded-xl border border-[#E5E0D2] overflow-hidden min-h-[420px] lg:min-h-[360px] 2xl:min-h-0 flex-col`}>
          <div className="p-4 border-b border-[#E5E0D2]">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold">Vorschau & Details</p>
          </div>
          <div className="p-4 overflow-y-auto min-h-0 flex-1">
            {selected ? (
              <div className="space-y-4">
                <div className="rounded-2xl border border-[#E5E0D2] bg-[#FAF8F2] overflow-hidden">
                  <div className="aspect-[4/3] flex items-center justify-center">
                    {selected.type === 'pdf' ? (
                      <div className="text-center px-3">
                        <FileText className="w-12 h-12 mx-auto text-[#C03A1A]" />
                        <p className="mt-2 text-[12px] font-semibold text-[#1F1D1A]">PDF</p>
                      </div>
                    ) : (
                      <img src={fullUrl(selected.url)} alt={metaDraft.alt?.de || metaDraft.alt?.nl || selected.filename} className="w-full h-full object-contain" />
                    )}
                  </div>
                </div>
                    <div>
                      <p className="font-semibold text-[14px] text-[#1F1D1A] break-words">{selected.filename}</p>
                      <p className="mt-2 font-mono text-[11px] text-[#5C5852] break-all">/{selected.path}</p>
                      <p className="mt-2 text-[11.5px] text-[#5C5852]">{fmtSize(selected.size)} - {fmtDate(selected.modified_at)}</p>
                    </div>
                    <MediaAiSummary meta={metaDraft} />
                    <div className="rounded-2xl border border-[#E5E0D2] bg-[#FAF8F2] p-4 space-y-4">
                      <div className="space-y-3">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C5852] font-semibold">Bild-SEO</p>
                          <p className="text-[11.5px] text-[#5C5852] mt-1">Alt-Tag, Beschreibung und sprechender Dateiname.</p>
                        </div>
                        <div className="grid grid-cols-1 gap-2">
                          <button
                            type="button"
                            onClick={generateSelectedMeta}
                            disabled={metaGenerating || selected.type === 'pdf'}
                            className="pill justify-center"
                            title={selected.type === 'pdf' ? 'KI-Vorschlag ist nur fuer Bilder verfuegbar.' : 'Bild mit KI analysieren'}
                          >
                            {metaGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            KI analysieren
                          </button>
                          <button type="button" onClick={saveSelectedMeta} disabled={metaSaving} className="pill pill-leaf justify-center">
                            {metaSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                            Speichern
                          </button>
                        </div>
                      </div>
                      {META_LANGS.map(([code, label]) => (
                        <div key={code} className="rounded-xl bg-white border border-[#E5E0D2] p-3 space-y-3">
                          <p className="text-[11px] uppercase tracking-[0.14em] text-[#5C5852] font-semibold">{label}</p>
                          <label className="block">
                            <span className="block text-[11px] font-semibold text-[#5C5852] mb-1">Titel / Bildname</span>
                            <input
                              value={metaDraft.title?.[code] || ''}
                              onChange={(event) => updateMetaDraft('title', code, event.target.value)}
                              className="w-full rounded-xl border border-[#E5E0D2] bg-white px-3 py-2 text-[12px] outline-none focus:border-[#FF681C]"
                              placeholder="Kurz und eindeutig"
                            />
                          </label>
                          <label className="block">
                            <span className="block text-[11px] font-semibold text-[#5C5852] mb-1">Alt-Tag</span>
                            <input
                              value={metaDraft.alt?.[code] || ''}
                              onChange={(event) => updateMetaDraft('alt', code, event.target.value)}
                              maxLength={180}
                              className="w-full rounded-xl border border-[#E5E0D2] bg-white px-3 py-2 text-[12px] outline-none focus:border-[#FF681C]"
                              placeholder="Was ist auf dem Bild zu sehen?"
                            />
                            <span className="mt-1 block text-[10.5px] text-[#5C5852]">{(metaDraft.alt?.[code] || '').length}/180 Zeichen</span>
                          </label>
                          <label className="block">
                            <span className="block text-[11px] font-semibold text-[#5C5852] mb-1">Bildbeschreibung</span>
                            <textarea
                              value={metaDraft.description?.[code] || ''}
                              onChange={(event) => updateMetaDraft('description', code, event.target.value)}
                              rows={3}
                              maxLength={700}
                              className="w-full resize-y rounded-xl border border-[#E5E0D2] bg-white px-3 py-2 text-[12px] outline-none focus:border-[#FF681C]"
                              placeholder="Beschreibung fuer Google, KI-Systeme und interne Suche"
                            />
                            <span className="mt-1 block text-[10.5px] text-[#5C5852]">{(metaDraft.description?.[code] || '').length}/700 Zeichen</span>
                          </label>
                        </div>
                      ))}
                      <label className="block">
                        <span className="block text-[11px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">Tags</span>
                        <input
                          value={(metaDraft.tags || []).join(', ')}
                          onChange={(event) => updateTags(event.target.value)}
                          className="w-full rounded-xl border border-[#E5E0D2] bg-white px-3 py-2 text-[12px] outline-none focus:border-[#FF681C]"
                          placeholder="fliegengitter, schiebetuer, terrasse"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setDialog({ kind: 'rename', item: selected, label: 'SEO-Dateiname', initial: seoFilename || suggestSeoFilename(selected, metaDraft) })}
                        className="pill w-full justify-center"
                      >
                        <Edit3 className="w-4 h-4" /> {seoFilename ? 'KI-Dateiname uebernehmen' : 'Dateiname aus Beschreibung vorschlagen'}
                      </button>
                      {seoFilename && (
                        <p className="rounded-xl bg-white border border-[#E5E0D2] px-3 py-2 text-[11px] text-[#5C5852] break-all">
                          KI-Dateiname: <span className="font-mono text-[#1F1D1A]">{seoFilename}</span>
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => copyUrl(selected)} className="pill justify-center"><Copy className="w-4 h-4" /> URL</button>
                      <a href={fullUrl(selected.download_url || selected.url)} className="pill justify-center" download><Download className="w-4 h-4" /> Download</a>
                      <button type="button" onClick={() => setDialog({ kind: 'rename', item: selected, label: 'Neuer Dateiname', initial: selected.filename })} className="pill justify-center"><Edit3 className="w-4 h-4" /> Name</button>
                  <button type="button" onClick={() => setDialog({ kind: 'move', item: selected, label: 'Zielordner', initial: selected.folder || '' })} className="pill justify-center"><MoveRight className="w-4 h-4" /> Verschieben</button>
                </div>
                <button type="button" onClick={() => deleteFile(selected)} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#C03A1A]/10 text-[#C03A1A] text-[12.5px] font-semibold hover:bg-[#C03A1A] hover:text-white">
                  <Trash2 className="w-4 h-4" /> Datei loeschen
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-dashed border-[#E5E0D2] bg-[#FAF8F2] p-5 text-center">
                  <ImageIcon className="w-9 h-9 mx-auto text-[#F26B1A]" />
                  <p className="mt-3 font-display text-[20px] text-[#1F1D1A]">Kein Element ausgewaehlt</p>
                  <p className="mt-2 text-[12.5px] text-[#5C5852] leading-relaxed">Waehle eine Datei in der Mitte aus, um Vorschau und Aktionen zu sehen.</p>
                </div>
                <UploadZone
                  path={currentPath}
                  onUploaded={(items) => { showToast(`${items.length} Datei${items.length === 1 ? '' : 'en'} hochgeladen.`); load(currentPath); }}
                  onError={(msg) => showToast(msg, 'err')}
                />
              </div>
            )}
          </div>
        </aside>
      </div>

      {dialog && (
        <Modal title={dialog.kind === 'folder' ? 'Ordner anlegen' : dialog.kind === 'move' ? 'Verschieben' : 'Umbenennen'} onClose={() => setDialog(null)}>
          <form onSubmit={submitDialog} className="space-y-4">
            <label className="block">
              <span className="block text-[11.5px] uppercase tracking-[0.14em] font-semibold text-[#5C5852] mb-1.5">
                {dialog.label || 'Ordnername'}
              </span>
              <input
                name="value"
                defaultValue={dialog.initial || ''}
                autoFocus
                list={dialog.kind === 'move' ? 'media-folder-targets' : undefined}
                placeholder={dialog.kind === 'move' ? 'z.B. 01_Fliegengitter oder leer fuer Root' : ''}
                className="w-full px-3 py-2.5 rounded-lg border-2 border-[#E5E0D2] focus:border-[#F26B1A] focus:outline-none text-[14px]"
              />
              {dialog.kind === 'move' && (
                <datalist id="media-folder-targets">
                  <option value="" label="/medienpool" />
                  {moveTargetOptions.map((folder) => (
                    <option key={folder.path} value={folder.path} label={`/medienpool/${folder.path}`} />
                  ))}
                </datalist>
              )}
            </label>
            {dialog.kind === 'move' && (
              <div className="rounded-xl border border-[#F2DFB3] bg-[#FFF8F0] p-3 text-[12px] text-[#5C5852] leading-relaxed">
                <p className="font-semibold text-[#1F1D1A] mb-1">Wichtig: Verschieben aendert den Pfad.</p>
                <p>Bereits in Seiten verwendete Bilder zeigen weiter auf die alte URL. Danach bitte im Seiteneditor neu auswaehlen oder nicht verschieben, wenn das Bild bereits live genutzt wird.</p>
                <p className="mt-2">Leer bedeutet Hauptordner. Das Eingabefeld schlaegt vorhandene Ordner vor.</p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDialog(null)} className="pill">Abbrechen</button>
              <button type="submit" className="pill pill-leaf">Speichern</button>
            </div>
          </form>
        </Modal>
      )}

      {toast && (
        <div data-testid="media-toast"
          className={`fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-full shadow-2xl text-[12.5px] font-semibold inline-flex items-center gap-2 ${
            toast.kind === 'err' ? 'bg-[#C03A1A] text-white' : 'bg-[#1F1D1A] text-white'
          }`}>
          {toast.kind === 'err' ? <X className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}
